import { apiClient, apiOrigin, type RequestOptions } from './apiClient';

export const developmentStatuses = [
  'TODO',
  'IN_PROGRESS',
  'BLOCKED',
  'READY_FOR_REVIEW',
  'ACCEPTED',
] as const;
export type DevelopmentStatus = (typeof developmentStatuses)[number];
export interface DevelopmentSource {
  id: string;
  fileName: string;
  textSha256: string;
  importedAt: string;
  importedById: string;
  itemCount: number;
}
export interface DevelopmentItem {
  id: string;
  reference: string;
  title: string;
  module: string;
  description: string;
  owner: string;
  dueDate: string | null;
  status: DevelopmentStatus;
  notes: string;
  evidenceReference: string;
  sourceId: string;
  sourceRow: number;
  sourceValues: Record<string, string>;
  acceptedAt: string | null;
  acceptedById: string | null;
  updatedAt: string;
}
export interface DevelopmentChecklist {
  version: number;
  updatedAt: string | null;
  sources: DevelopmentSource[];
  items: DevelopmentItem[];
  summary: {
    total: number;
    accepted: number;
    inProgress: number;
    blocked: number;
    readyForReview: number;
    outstanding: number;
    overdue: number;
    percentAccepted: number;
  };
}
export interface ChecklistImportPreview {
  fileName: string;
  textSha256: string;
  items: Array<
    Omit<DevelopmentItem, 'id' | 'sourceId' | 'acceptedAt' | 'acceptedById' | 'updatedAt'>
  >;
  warnings: string[];
}
export interface ChecklistImportInput {
  expectedVersion: number;
  fileName: string;
  content: string;
}
export interface ChecklistPatchInput {
  expectedVersion: number;
  status: DevelopmentStatus;
  owner: string;
  dueDate: string | null;
  notes: string;
  evidenceReference: string;
}
const base = `${apiOrigin}/api/build-status/checklist`;
function unwrap<T>(result: { ok: boolean; data: T | null; error: string | null }): T {
  if (!result.ok || !result.data)
    throw new Error(result.error ?? 'The development checklist could not be loaded.');
  return result.data;
}
export const developmentChecklistClient = {
  async read(options?: RequestOptions): Promise<DevelopmentChecklist> {
    return unwrap(await apiClient.get<DevelopmentChecklist>(base, options));
  },
  async preview(
    input: Omit<ChecklistImportInput, 'expectedVersion'>,
    options?: RequestOptions,
  ): Promise<ChecklistImportPreview> {
    return unwrap(await apiClient.post<ChecklistImportPreview>(`${base}/preview`, input, options));
  },
  async import(
    input: ChecklistImportInput,
    options?: RequestOptions,
  ): Promise<DevelopmentChecklist> {
    return unwrap(await apiClient.post<DevelopmentChecklist>(`${base}/import`, input, options));
  },
  async update(
    itemId: string,
    input: ChecklistPatchInput,
    options?: RequestOptions,
  ): Promise<DevelopmentChecklist> {
    return unwrap(
      await apiClient.patch<DevelopmentChecklist>(
        `${base}/items/${encodeURIComponent(itemId)}`,
        input,
        options,
      ),
    );
  },
};
